import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';

/* ── TikTok icon (not in lucide-react) ── */
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z" />
  </svg>
);

/* ── WhatsApp icon ── */
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

const socialLinks = [
  { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/servitiumpro/' },
  { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61578237311186' },
  { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/101705843/admin/dashboard/' },
  { name: 'WhatsApp', icon: WhatsAppIcon, href: 'https://wa.me/message/RAX52YKJZCL4A1', isCustom: true },
  { name: 'YouTube', icon: Youtube, href: 'https://www.youtube.com/@SERVITIUMPRO' },
  { name: 'TikTok', icon: TikTokIcon, href: 'https://www.tiktok.com/@servitium_pro?_r=1&_t=ZS-93oVmKn2IS4', isCustom: true },
];

const businessLinks = [
  { name: 'The Mission', href: '/mission' },
  { name: 'The Infrastructure', href: '/#the-infrastructure' },
  { name: 'How It Works', href: '/#how-it-works' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Join free', href: '/get' },
];

const customerLinks = [
  { name: 'How it works', href: '/customers#how-it-works' },
  { name: 'Trust', href: '/customers#trust' },
  { name: 'FAQ', href: '/customers#faq' },
  { name: 'Download', href: '/customers/get' },
];

const supportLinks = [
  { name: 'Contact Us', href: '/contact-us' },
  { name: 'Privacy Policy', href: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=servitium-marketing', external: true },
  { name: 'Terms of Service', href: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=servitium-marketing', external: true },
];

export const Footer = () => (
  <footer
    className="relative w-full"
    style={{ background: 'radial-gradient(circle at top center, #880808 0%, #880808 100%)' }}
    data-testid="footer"
  >
    {/* Inject shimmer keyframes */}
    <style>{`
      @keyframes shimmerSweep {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
      }
    `}</style>

    {/* Gold accent line */}
    <div
      className="w-full h-[1.5px]"
      style={{ background: 'linear-gradient(90deg, transparent 0%, #FAB515 50%, transparent 100%)' }}
    />

    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-8">
      {/* Main grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-x-12 gap-y-10 mb-12">

        {/* LEFT COLUMN */}
        <div className="lg:col-span-6">
          {/* Brand */}
          <Link to="/" className="inline-block focus:outline-none" data-testid="footer-logo">
            <span className="text-2xl font-bold tracking-tight uppercase text-white">
              Servitium
            </span>
          </Link>

          {/* Description */}
          <p className="text-[13px] text-white/90 max-w-sm leading-relaxed font-light mt-4">
            Digital infrastructure for solo and micro-businesses.
          </p>

          {/* Shimmer slogan */}
          <p
            className="mt-5 text-[12px] font-bold tracking-[0.2em] uppercase"
            style={{
              background: 'linear-gradient(90deg, #D4AF37, #fff, #D4AF37, #fff, #D4AF37)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmerSweep 4s linear infinite',
              textShadow: '0 0 8px rgba(212,175,55,0.15)',
            }}
            data-testid="footer-slogan"
          >
            Every solo operator competes on quality, not price.
          </p>

          {/* Wahkohtowin block */}
          <div className="mt-4 max-w-xs">
            <p className="text-[11px] leading-relaxed text-gray-300/80 font-medium">
              Honoring ᐋᐦᑰᐦᑐᐏᐣ (Wahkohtowin)
            </p>
            <p className="text-[11px] leading-relaxed text-gray-300/80 font-medium mt-0.5 opacity-75">
              Believing that all things are connected.
            </p>
          </div>

          {/* Social icons */}
          <div className="flex items-center gap-3 pt-2 mt-3">
            {socialLinks.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 rounded border border-white/20 bg-white/5 flex items-center justify-center text-white transition-all duration-200 hover:border-[#D4AF37] hover:text-[#D4AF37] hover:shadow-[0_0_8px_rgba(212,175,55,0.3)]"
                aria-label={s.name}
                data-testid={`social-${s.name.toLowerCase()}`}
              >
                {s.isCustom ? <s.icon /> : <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </a>
            ))}
          </div>
        </div>

        {/* FOR BUSINESSES COLUMN */}
        <div className="lg:col-span-2 lg:pl-12">
          <h3
            className="text-[11px] font-bold tracking-[0.2em] uppercase opacity-90 mb-6"
            style={{ color: '#D4AF37' }}
          >
            FOR BUSINESSES
          </h3>
          <ul className="space-y-[7px]">
            {businessLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.href}
                  className="text-[12.5px] text-white/80 hover:text-[#D4AF37] transition-colors duration-200"
                  data-testid={`footer-link-business-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* FOR CUSTOMERS COLUMN */}
        <div className="lg:col-span-2">
          <h3
            className="text-[11px] font-bold tracking-[0.2em] uppercase opacity-90 mb-6"
            style={{ color: '#D4AF37' }}
          >
            FOR CUSTOMERS
          </h3>
          <ul className="space-y-[7px]">
            {customerLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.href}
                  className="text-[12.5px] text-white/80 hover:text-[#D4AF37] transition-colors duration-200"
                  data-testid={`footer-link-customers-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* SUPPORT COLUMN */}
        <div className="lg:col-span-2">
          <h3
            className="text-[11px] font-bold tracking-[0.2em] uppercase opacity-90 mb-6"
            style={{ color: '#D4AF37' }}
          >
            SUPPORT
          </h3>
          <ul className="space-y-[7px]">
            {supportLinks.map((link) => (
              <li key={link.name}>
                {link.external ? (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12.5px] text-white/80 hover:text-[#D4AF37] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    to={link.href}
                    className="text-[12.5px] text-white/80 hover:text-[#D4AF37] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 pt-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[10px] text-white/40 tracking-wide font-medium" data-testid="footer-location">
            Built with care in Beautiful British Columbia, Canada.
          </p>
          <p className="text-[10px] text-white/40 tracking-widest font-medium uppercase" data-testid="footer-copyright">
            &copy; 2026 Servitium Technology Inc. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
